import { SREV2 } from '../api/pushGenie';
import {
    ON_ADD_PT,
    ON_EDIT_PATIENT,
    ON_ENTER_VITALS,
    ON_REQUEST_MEDICAL_RECORDS,
} from '../bridge/events/types';
import {
    ALL_PATIENTS_ROUTE,
    GO_TO_ADD_NEW_PATIENT,
    GO_TO_ANALYTICS_DASHBOARD,
    GO_TO_MY_CALENDAR,
    GO_TO_MY_PROFILE,
    GO_TO_SETTINGS,
    HE_DASHBOARD_ROUTE,
    PAYMENT_DASHBOARD_ROUTE,
    PRESCRIPTION_PAD_V2_ROUTE,
} from '../routes/paths';
import { TOGGLE_GLOBAL_SEARCH } from '../store/actions/session';
import { CREATE_VISIT, DOWNLOAD_PT_DIR } from '../store/actions/visit';

export const RESTRICTIONS: {
    key: SREV2;
    title: string;
    routes: Set<string>;
    actions: Set<string>;
    views: Set<string>;
}[] = [
    // ☝️ first six are existing ones, ALL_PT to EDIT_RECEIPT
    {
        key: SREV2.ALL_PT,
        title: 'Access to All Patients page',
        routes: new Set([ALL_PATIENTS_ROUTE]),
        actions: new Set(),
        views: new Set([SREV2.ALL_PT]),
    },
    {
        key: SREV2.START_VISIT,
        title: 'Ability to Start a Visit',
        routes: new Set([PRESCRIPTION_PAD_V2_ROUTE]),
        actions: new Set([CREATE_VISIT]),
        views: new Set([SREV2.START_VISIT]),
    },
    {
        key: SREV2.PAY_DASH,
        title: 'Access to Payment Dashboard Page',
        routes: new Set([PAYMENT_DASHBOARD_ROUTE]),
        actions: new Set(),
        views: new Set([SREV2.PAY_DASH]),
    },
    {
        key: SREV2.DOWNLOAD_PATIENT_EXCEL,
        title: 'Access to Download Patient Database Excel',
        routes: new Set(),
        actions: new Set([DOWNLOAD_PT_DIR]),
        views: new Set([SREV2.DOWNLOAD_PATIENT_EXCEL]),
    },
    {
        key: SREV2.PAY_DASH_BEYOND_TODAY,
        title: 'Access to Payment Dashboard Page beyond today',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.PAY_DASH_BEYOND_TODAY]),
    },
    {
        /**
         * @deprecated
         */
        key: SREV2.EDIT_RECEIPT,
        title: 'Ability to Edit Receipt',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.EDIT_RECEIPT, SREV2.RECEIPT_EDIT]),
    },
    {
        key: SREV2.PATIENT_REGISTRATION,
        title: 'Permission to add a patient to the queue',
        routes: new Set([GO_TO_ADD_NEW_PATIENT(''), GO_TO_ADD_NEW_PATIENT(':cId')]),
        actions: new Set([ON_ADD_PT]),
        views: new Set([SREV2.PATIENT_REGISTRATION]),
    },
    {
        key: SREV2.PATIENT_DATABASE,
        title: 'Permission to edit or update patient information after registration',
        routes: new Set(),
        actions: new Set([ON_EDIT_PATIENT]),
        views: new Set([SREV2.PATIENT_DATABASE]),
    },
    {
        key: SREV2.VITALS_COLLECTION,
        title: 'Permission to update patient vitals',
        routes: new Set(),
        actions: new Set([ON_ENTER_VITALS]),
        views: new Set([SREV2.VITALS_COLLECTION]),
    },
    {
        key: SREV2.CAN_REQUEST_RECORDS,
        title: 'Permission to request medical records from patients',
        routes: new Set(),
        actions: new Set([ON_REQUEST_MEDICAL_RECORDS]),
        views: new Set([SREV2.CAN_REQUEST_RECORDS]),
    },
    {
        key: SREV2.AVAILABILITY,
        title: 'Permission to edit the availability of any doctor',
        routes: new Set([GO_TO_MY_CALENDAR]),
        actions: new Set(),
        views: new Set([SREV2.AVAILABILITY]),
    },
    {
        key: SREV2.SKU_CREATE,
        title: 'Permission to create services',
        routes: new Set([GO_TO_SETTINGS('manage-services')]),
        actions: new Set(),
        views: new Set([SREV2.SKU_CREATE]),
    },
    {
        key: SREV2.SKU_EDIT,
        title: 'Permission to edit services',
        routes: new Set([GO_TO_SETTINGS('manage-services')]),
        actions: new Set(),
        views: new Set([SREV2.SKU_EDIT]),
    },
    {
        key: SREV2.SERVICES,
        title: 'Permission to manage services (edit, deactivate, bulk upload and more)',
        routes: new Set([GO_TO_SETTINGS('manage-services')]),
        actions: new Set(),
        views: new Set([SREV2.SERVICES]),
    },
    {
        key: SREV2.RECEIPT_EDIT,
        title: 'Permission to edit receipt',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.RECEIPT_EDIT, SREV2.EDIT_RECEIPT]),
    },
    {
        key: SREV2.DAILY_FINANCE_OVERVIEW_STRIP,
        title: 'Permission to access the daily billing summary',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.DAILY_FINANCE_OVERVIEW_STRIP]),
    },
    {
        key: SREV2.PACKAGES,
        title: 'Permission to create, edit or delete packages',
        routes: new Set([
            GO_TO_SETTINGS('manage-treatment-packages'),
            GO_TO_SETTINGS('manageTreatmentPackages'),
        ]),
        actions: new Set(),
        views: new Set([SREV2.PACKAGES]),
    },
    {
        key: SREV2.DOWNLOAD_QUEUE,
        title: 'Permission to download the queue and generate reports',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.DOWNLOAD_QUEUE]),
    },
    {
        key: SREV2.SETTINGS_AND_PREFERENCES,
        title: 'Permission to access and modify settings and preferences for the business',
        routes: new Set([GO_TO_SETTINGS(':subRoute')]),
        actions: new Set(),
        views: new Set([SREV2.SETTINGS_AND_PREFERENCES]),
    },
    {
        key: SREV2.PROFILE_MANAGEMENT,
        title: 'Permission to edit and manage profiles of any doctor',
        routes: new Set([GO_TO_MY_PROFILE]),
        actions: new Set(),
        views: new Set([SREV2.PROFILE_MANAGEMENT]),
    },
    {
        key: SREV2.ROLES_AND_PERMISSIONS,
        title: 'Permission to access and manage roles and permissions',
        routes: new Set([GO_TO_SETTINGS('staffRestriction')]),
        actions: new Set(),
        views: new Set([SREV2.ROLES_AND_PERMISSIONS]),
    },
    {
        key: SREV2.ANALYTICS,
        title: 'Permission to access analytics',
        routes: new Set([GO_TO_ANALYTICS_DASHBOARD()]),
        actions: new Set(),
        views: new Set([SREV2.ANALYTICS]),
    },
    {
        key: SREV2.PAYOUT_REPORTS,
        title: 'Permission to access payout reports',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.PAYOUT_REPORTS]),
    },
    {
        key: SREV2.CAN_CREATE_RECEIPT_WITH_APT,
        title: 'Allow user to create receipt with appointment',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.CAN_CREATE_RECEIPT_WITH_APT]),
    },
    {
        key: SREV2.PAYMENT_ID_IN_RECEIPT,
        title: 'Payment ID in receipt will be mandatory while creating a receipt',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.PAYMENT_ID_IN_RECEIPT]),
    },
    {
        key: SREV2.HXNG_ORDERS,
        title: 'Access to Health Exchange Orders Dashboard',
        routes: new Set([HE_DASHBOARD_ROUTE]),
        actions: new Set(),
        views: new Set([SREV2.HXNG_ORDERS]),
    },
    {
        key: SREV2.EDIT_RX,
        title: 'Ability to Edit Prescription',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.EDIT_RX]),
    },
    {
        key: SREV2.LEFT_BLOCK_ON_NEW_QUEUE,
        title: 'Access to Left Block on New Queue',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.LEFT_BLOCK_ON_NEW_QUEUE]),
    },
    {
        key: SREV2.GO_TO_OLD_QUEUE,
        title: 'Ability to Switch to Old Queue',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.GO_TO_OLD_QUEUE]),
    },
    {
        key: SREV2.MY_ACCOUNT,
        title: 'Access to My Account Page',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.MY_ACCOUNT]),
    },
    {
        key: SREV2.PATIENT_MOBILE_NUMBER,
        title: 'Ability to view Patient Mobile Number',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.PATIENT_MOBILE_NUMBER]),
    },
    {
        key: SREV2.DELETE_RECEIPT,
        title: 'Ability to Delete Receipt',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.DELETE_RECEIPT]),
    },
    {
        key: SREV2.ADD_PATIENT_ADDRESS,
        title: 'Ability to Add Patient Address',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.ADD_PATIENT_ADDRESS]),
    },
    {
        key: SREV2.UPLOAD_MEDICAL_RECORDS,
        title: 'Ability to Upload Patient Medical Records',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.UPLOAD_MEDICAL_RECORDS]),
    },
    {
        key: SREV2.ABHA,
        title: 'Ability to Create ABHA',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.ABHA]),
    },
    {
        key: SREV2.SA,
        title: 'Ability to View Self Assessment',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.SA]),
    },
    {
        key: SREV2.TREATMENT,
        title: 'Ability to Create Treatment',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.TREATMENT]),
    },
    {
        key: SREV2.REFER_PATIENT_TO_DOCTOR,
        title: 'Ability to Refer a Patient to Doctor',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.REFER_PATIENT_TO_DOCTOR]),
    },
    {
        key: SREV2.SEND_GOOGLE_REVIEW,
        title: 'Ability to Send Google Review',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.SEND_GOOGLE_REVIEW]),
    },
    {
        key: SREV2.SEND_ATTACHMENT,
        title: 'Ability to Send Attachment',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.SEND_ATTACHMENT]),
    },
    {
        key: SREV2.REQUEST_VITALS,
        title: 'Ability to Request Vitals',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.REQUEST_VITALS]),
    },
    {
        key: SREV2.SEND_PAYMENT_LINK,
        title: 'Ability to Send Payment Link',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.SEND_PAYMENT_LINK]),
    },
    {
        key: SREV2.SEND_RX_VIA_OWN_WA,
        title: 'Ability to Send Prescription via Own WhatsApp',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.SEND_RX_VIA_OWN_WA]),
    },
    {
        key: SREV2.SEND_RX,
        title: 'Ability to Send Rx',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.SEND_RX]),
    },
    {
        key: SREV2.CREATE_CUSTOM_QUEUE,
        title: 'Ability to Create Custom Queue',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.CREATE_CUSTOM_QUEUE]),
    },
    {
        key: SREV2.DOC_ASSIST,
        title: 'Access to Doc Assist',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.DOC_ASSIST]),
    },
    {
        key: SREV2.CREATE_MEDICAL_DOCUMENTS,
        title: 'Ability to Create Medical Documents (medical certificate, consent form etc.)',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.CREATE_MEDICAL_DOCUMENTS]),
    },
    {
        key: SREV2.EKA_CREDITS,
        title: 'Access to Eka Credits',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.EKA_CREDITS]),
    },
    {
        key: SREV2.MERGE_PATIENTS,
        title: 'Ability to Merge Patients',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.MERGE_PATIENTS]),
    },
    {
        key: SREV2.APPOINTMENT_WIDGET,
        title: 'Access to Appointment Widget (In Global Search)',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.APPOINTMENT_WIDGET]),
    },
    {
        key: SREV2.PT_DETAILS_VISIT_ACTIONS,
        title:
            'Ability to Perform Visit Actions from Patient Details Page (Start visit, Resume visit etc.)',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.PT_DETAILS_VISIT_ACTIONS]),
    },
    {
        key: SREV2.EDIT_QUEUE,
        title: 'Ability to Edit Queue',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.EDIT_QUEUE]),
    },
    {
        key: SREV2.CAN_CREATE_RX_TEMPLATE,
        title: 'Ability to Create Prescription Template',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.CAN_CREATE_RX_TEMPLATE]),
    },
    {
        key: SREV2.CUSTOMIZE_RX_TEMPLATE,
        title: 'Ability to Customize Prescription Template',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.CUSTOMIZE_RX_TEMPLATE]),
    },
    {
        key: SREV2.REQUEST_TRAINING,
        title: 'Ability to Request Training',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.REQUEST_TRAINING]),
    },
    {
        key: SREV2.BUSINESS_ANALYTICS,
        title: 'Permission to access business analytics',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.BUSINESS_ANALYTICS]),
    },
    {
        key: SREV2.GENIE_DASHBOARD,
        title: 'Permission to access genie dashboard',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.GENIE_DASHBOARD]),
    },
    {
        key: SREV2.PATIENT_REFERRALS_DASHBOARD,
        title: 'Permission to access patient referrals dashboard',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.PATIENT_REFERRALS_DASHBOARD]),
    },
    {
        key: SREV2.APPOINTMENTS_CALENDAR_VIEW,
        title: 'Access to Appointments in Calendar View',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.APPOINTMENTS_CALENDAR_VIEW]),
    },
    {
        key: SREV2.INVITE_CODE,
        title: 'Permission to View and Share Invite Code',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.INVITE_CODE]),
    },
    {
        key: SREV2.DELETE_APPOINTMENT,
        title: 'Permission to Delete Appointment',
        routes: new Set(),
        actions: new Set(),
        views: new Set([SREV2.DELETE_APPOINTMENT]),
    },
    {
        // this can be generated dynamically based on apt fields
        key: SREV2.APT_FIELD_PAYMENT_STATUS,
        title: 'Permission to Edit Payment Status in Appointment',
        routes: new Set(),
        actions: new Set([SREV2.APT_FIELD_PAYMENT_STATUS]),
        views: new Set(),
    },
    {
        key: SREV2.GLOBAL_SEARCH,
        title: 'Global Search',
        routes: new Set(),
        actions: new Set([TOGGLE_GLOBAL_SEARCH]),
        views: new Set([SREV2.GLOBAL_SEARCH]),
    },
];
